import React, { useMemo, useState } from 'react'
import { getMessage } from 'lib/translator'
import MultiImageDropzone from './MultiImageDropzone'
import { HERO_IMAGE_SIDE, MAX_IMAGE_SIDE, MIN_IMAGE_SIDE } from '../constants'
import {
  getUploadErrorMessage,
  isRetryableUploadFailure,
  isUploadRetryAllowed,
  uploadProductMediaInBatches,
} from '../utils'

function createPendingResult(file, retryCount = 0) {
  return {
    file,
    status: 'pending',
    message: '',
    transformedImageUrl: '',
    retryCount,
  }
}

function applyFileStatusUpdate(item, update) {
  if (update.status === 'uploading') {
    return { ...item, status: 'uploading' }
  }

  if (update.status === 'success') {
    return {
      ...item,
      status: 'success',
      transformedImageUrl: update.transformedImageUrl,
      message:
        update.message ||
        getMessage('catalogue.skuMediaUpload.bulkUpload.upload.success'),
    }
  }

  return {
    ...item,
    status: 'error',
    message: getUploadErrorMessage(update.error),
    retryable: isRetryableUploadFailure(update.error),
  }
}

function canRetryResult(item) {
  return (
    item.status === 'error' &&
    item.retryable &&
    isUploadRetryAllowed(item.retryCount ?? 0)
  )
}

function MediaBulkUpload() {
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState([])

  const retryableFailedResults = useMemo(
    () => results.filter(canRetryResult),
    [results]
  )

  const uploadFiles = async (filesToUpload, { mergeResults = false } = {}) => {
    if (!filesToUpload.length) {
      return
    }

    setUploading(true)

    if (mergeResults) {
      const retryNames = new Set(filesToUpload.map((file) => file.name))
      setResults((prev) =>
        prev.map((item) =>
          retryNames.has(item.file.name)
            ? createPendingResult(item.file, (item.retryCount ?? 0) + 1)
            : item
        )
      )
    } else {
      setResults(filesToUpload.map((file) => createPendingResult(file)))
    }

    await uploadProductMediaInBatches(filesToUpload, {
      onFileStatusChange: (index, update) => {
        const fileName = filesToUpload[index].name

        setResults((prev) =>
          prev.map((item) =>
            item.file.name === fileName
              ? applyFileStatusUpdate(item, update)
              : item
          )
        )
      },
    })

    setUploading(false)
  }

  const handleUploadAll = async () => {
    const filesToUpload = files
    await uploadFiles(filesToUpload)
    setFiles([])
  }

  const handleRetryFailed = async () => {
    const filesToRetry = retryableFailedResults.map((item) => item.file)
    await uploadFiles(filesToRetry, { mergeResults: true })
  }

  return (
    <>
      <p className="sku-media-tab-description">
        {getMessage('catalogue.skuMediaUpload.bulkUpload.description', {
          fileNamePattern: `{sku}_{${MIN_IMAGE_SIDE}-${MAX_IMAGE_SIDE}}.jpg`,
          heroFileNamePattern: `{sku}_${HERO_IMAGE_SIDE}.jpg`,
        })}
      </p>

      <MultiImageDropzone
        files={files}
        onFilesChange={setFiles}
        disabled={uploading}
      />

      {files.length > 0 && (
        <div className="sku-media-upload-actions">
          <button
            type="button"
            className="button primary"
            disabled={uploading}
            data-testid="sku-media-upload-all"
            onClick={handleUploadAll}
          >
            {uploading
              ? getMessage('catalogue.skuMediaUpload.bulkUpload.uploading')
              : getMessage('catalogue.skuMediaUpload.bulkUpload.uploadButton', {
                  count: files.length,
                })}
          </button>
        </div>
      )}

      {results.length > 0 && (
        <div
          className="sku-media-upload-results"
          data-testid="sku-media-upload-results"
        >
          <div className="sku-media-upload-results-header">
            <h3>
              {getMessage('catalogue.skuMediaUpload.bulkUpload.resultsHeading')}
            </h3>
            {retryableFailedResults.length > 0 && (
              <button
                type="button"
                className="button secondary"
                disabled={uploading}
                data-testid="sku-media-retry-failed"
                onClick={handleRetryFailed}
              >
                {uploading
                  ? getMessage(
                      'catalogue.skuMediaUpload.bulkUpload.retryingFailed'
                    )
                  : getMessage(
                      'catalogue.skuMediaUpload.bulkUpload.retryFailedButton',
                      { count: retryableFailedResults.length }
                    )}
              </button>
            )}
          </div>
          <table className="sku-media-results-table">
            <thead>
              <tr>
                <th>{getMessage('catalogue.skuMediaUpload.table.fileName')}</th>
                <th>{getMessage('catalogue.skuMediaUpload.table.status')}</th>
                <th>{getMessage('catalogue.skuMediaUpload.table.details')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((item) => (
                <tr
                  key={item.file.name}
                  className={`sku-media-result-${item.status}`}
                >
                  <td>{item.file.name}</td>
                  <td>{item.status}</td>
                  <td>
                    {item.transformedImageUrl ? (
                      <a
                        href={item.transformedImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.transformedImageUrl}
                      </a>
                    ) : (
                      item.message
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

export default MediaBulkUpload
